// @flow
import * as React from 'react';
import styled from 'styled-components';
import Box from 'components/Box';
import Icon from 'components/Icon';
import Link from 'components/Link';
import { PrimaryButton } from 'components/Button';
import LocationInputContainer from 'containers/LocationInputContainer';
import ListingTypeSelect from 'components/ListingTypeSelect';
import SearchQueryInputContainer from 'containers/SearchQueryInputContainer';
import TabContext from 'containers/TabContext';
import scrollIntoView from 'utils/scrollIntoView';
import { breakpoints, bp, colors, transition, mediaRetina } from '../theme';

const { useMemo } = React;

const SearchQueryInput = styled(SearchQueryInputContainer)`
  width: 100%;
  height: 100%;
`;

const StyledListingTypeSelect = styled(ListingTypeSelect)`
  && {
    height: 50px;
    select {
      height: 50px;
    }
    ${props =>
      props.$inPageHeader &&
      `
    @media (min-width: ${breakpoints[2]}) {
      width: 220px;
      border-radius: 4px;
      border: 1px solid transparent;
      background: ${colors.platinum};
      .react-select__control {
        border: solid 1px #c4c8cc;
        border-radius: 4px 0 0 4px;
        width: 220px;
        .react-select__value-container {
          height: auto;
          margin-left: 2px;
        }
      }
    }`}
  }
  & + * {
    svg {
      margin-top: 2px;
    }
  }
`;

const LocationContainer = styled.div`
  width: 100%;
  @media (min-width: ${breakpoints[2]}) {
    max-width: ${props => (props.inPageHeader ? '250px' : 'none')};
  }
`;

const StyledLocationInputContainer = styled(LocationInputContainer)`
  height: 100%;
`;

const SearchButton = styled(PrimaryButton)`
  width: 100%;
  border-radius: ${props => (props.$inPageHeader ? '4px' : '7px')};
  flex: 0 0 auto;
  height: 50px;
  transition: background ${transition};
  @media (min-width: ${breakpoints[2]}) {
    flex-shrink: 0;
    width: ${props =>
      props.$inPageHeader ? 'auto' : props.$showLogin ? '50%' : '100%'};
    margin: 0;
    padding: 0 16px;
  }
  @media (min-width: ${breakpoints[3]}) {
    padding: 0 40px;
  }
`;

const StyledSearchInputs = styled.form`
  && {
    flex-direction: column;
    align-items: center;
    width: 100%;
    margin: 0 auto;
    padding: 0;
    font-size: 18px;
    @media (min-width: ${breakpoints[1]}) {
      max-width: 400px;
    }
    @media (min-width: ${breakpoints[2]}) {
      max-width: 100%;
      width: auto;
    }
    ${props =>
      props.inPageHeader
        ? `
      display: flex;
      @media (min-width: ${breakpoints[2]}) {
        flex-direction: row;
      }

      > * {
        margin-top: 8px;
        border: 0 !important;
        @media (min-width: ${breakpoints[2]}) {
          margin-left: 8px;
          margin-top: 0;
        }
      }

      ${StyledLocationInputContainer} {
        width: 100%;
        @media (min-width: ${breakpoints[2]}) {
          max-width: 250px;
        }
      }

      .react-select__control {
        border: solid 1px #c4c8cc;
        border-bottom: none;
        border-radius: 4px 4px 0 0;
      }
    `
        : `
      > *, .react-select__control, input {
        width: 100%;
      }
    `}
    @media (max-width: ${bp[2] - 1}px) {
      > *, .react-select__control, input {
        width: 100%;
        max-width: none;
      }
    }

    input {
      &::-ms-clear {
        width: 0;
        height: 0;
      }
      &:focus {
        box-shadow: 0 0 0 1px ${colors.waterBlue}
          ${props => (props.inPageHeader ? 'inset' : '')};
        @media ${mediaRetina} {
          box-shadow: 0 0 0 1.5px ${colors.waterBlue}
            ${props => (props.inPageHeader ? 'inset' : '')};
        }
      }
    }
    input,
    button,
    .react-select__value-container,
    .react-select__control {
      font-size: inherit;
    }
    input,
    .react-select__control {
      height: 100%;
      border-radius: ${props => (props.inPageHeader ? '4px' : '7px')};
      border: ${props =>
        props.inPageHeader ? 0 : `1px solid ${colors.aluminum}`};
      transition: all ${transition};
      ${props =>
        props.inPageHeader &&
        `
        background: ${colors.platinum};
      `}
    }
    .react-select__value-label {
      color: ${colors.waterBlue} !important;
    }
    .react-select__control {
      overflow: visible;
    }
    .react-select__option {
      height: 49px;
    }
    .react-select__menu {
      max-height: none;
      margin-top: 3px;
      box-shadow: 0 0 6px rgba(0, 0, 0, 0.2);
      border: 0;
    }
    .react-select__single-value {
      color: ${colors.oceanBlue};
    }
    .react-select__control--menu-is-open {
      ${props =>
        props.inPageHeader &&
        `
        filter: drop-shadow(0 2px 6px rgba(0,0,0,0.1));
      `}
    }
    .react-select__control--is-focused {
      border-color: transparent;
      box-shadow: 0 0 0 1px ${colors.waterBlue}
        ${props => (props.inPageHeader ? 'inset' : '')};
      @media ${mediaRetina} {
        box-shadow: 0 0 0 1.5px ${colors.waterBlue}
          ${props => (props.inPageHeader ? 'inset' : '')};
      }
    }
    > * {
      height: 50px;
      box-sizing: border-box;
      margin-bottom: ${props => (props.inPageHeader ? 0 : '6px')};
      @media (min-width: ${breakpoints[2]}) {
        height: 50px;
        &:first-child {
          ${props =>
            !props.inPageHeader &&
            `
          .react-select__value-container,
          .react-select__option {
            padding-left: 16px !important;
          }
          .react-select__value-container {
            padding-right: 40px !important;
          }
          .react-select__option {
            padding-right: 28px;
          }`}
          .react-select__input {
            opacity: 0;
          }
        }
        .react-select__option {
          height: 49px;
          border-bottom-color: ${colors.aluminum};
        }
      }
    }
    ${props =>
      !props.isTabbing
        ? `
      input {
        &:focus {
          outline: 0;
        }
      }
    `
        : `
    `}
  }
`;

const scrollToTop = () => {
  scrollIntoView();
};

type DisplayedType = ListingType | StaffTypeFilter;

const SearchInputs = ({
  selectedType,
  listingTypes,
  onSearch,
  query,
  redirectParam,
  searchLocation,
  searchLocationDisabled,
  setListingType,
  setQuery,
  setSearchLocation,
  search,
  placeholder,
  xsPlaceholder,
  inPageHeader,
  clickLogin,
  clickSignup,
  showLogin,
  clearSearchInput,
  currentUserLoaded
}: {|
  selectedType: DisplayedType,
  listingTypes: Array<DisplayedType>,
  onSearch: Function,
  query: string,
  redirectParam?: string,
  searchLocation: ?SearchLocation,
  searchLocationDisabled?: boolean,
  setListingType: ListingType => void,
  setQuery: string => void,
  setSearchLocation: (?SearchLocation) => void,
  search: string => void,
  placeholder?: string,
  xsPlaceholder?: string,
  inPageHeader?: boolean,
  clickLogin?: Function,
  clickSignup?: Function,
  showLogin?: Function,
  clearSearchInput: () => void,
  currentUserLoaded?: boolean
|}) => {
  const locationInput = useMemo(
    () => (
      <StyledLocationInputContainer
        disabled={searchLocationDisabled}
        showLocationIcon
        showClearButton
        placeholder={getText('Everywhere')}
        locationText={searchLocation && searchLocation.text}
        onChange={setSearchLocation}
      />
    ),
    [searchLocation, searchLocationDisabled, setSearchLocation]
  );

  return (
    <TabContext.Consumer>
      {isTabbing => (
        <StyledSearchInputs
          onSubmit={e => {
            e.preventDefault();
            search(query);
            if (onSearch) {
              onSearch();
            }
          }}
          data-qa-id="search-inputs"
          inPageHeader={inPageHeader}
          isTabbing={isTabbing}
        >
          <Box
            component="label"
            display="block"
            height={
              inPageHeader
                ? '100%'
                : ['auto !important', null, null, '50px !important']
            }
          >
            <Box display="none">{getText('Select listing type')}</Box>
            <StyledListingTypeSelect
              value={selectedType}
              onChange={setListingType}
              listingTypes={listingTypes}
              $inPageHeader={inPageHeader}
            />
          </Box>

          <Box height="100%" width="100%" flex={[null, null, null, '1 1 auto']}>
            <SearchQueryInput
              value={query}
              query={query}
              placeholder={placeholder}
              xsPlaceholder={xsPlaceholder}
              data-qa-id="search-input"
              onChange={e => {
                setQuery(e.target.value);
              }}
              clearQuery={clearSearchInput}
            />
          </Box>

          <LocationContainer inPageHeader={inPageHeader}>
            {locationInput}
          </LocationContainer>

          <Box
            flex="0 0 auto"
            height={
              inPageHeader
                ? '100%'
                : ['auto !important', null, null, '50px !important']
            }
            display={['block', null, null, 'flex']}
            mt={!inPageHeader && 24}
          >
            <SearchButton
              disabled={!currentUserLoaded}
              type="submit"
              data-qa-id="search-button"
              $inPageHeader={inPageHeader}
              $showLogin={showLogin}
              onClick={scrollToTop}
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <Icon name="search" width={16} height={16} fill="white" />
                <Box ml="7px" mr="4px">
                  {getText('Search')}
                </Box>
              </Box>
            </SearchButton>
            {!inPageHeader && redirectParam && showLogin && (
              <Box
                flex="1 1 auto"
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                my={!inPageHeader && [24, null, null, 0]}
              >
                <Link href={`/login?${redirectParam}`} onClick={clickLogin}>
                  {getText('Log In')}
                </Link>
                <Box mx="8px" color={colors.silver}>
                  |
                </Box>
                <Link href={`/signup?${redirectParam}`} onClick={clickSignup}>
                  {getText('Sign up')}
                </Link>
              </Box>
            )}
          </Box>
        </StyledSearchInputs>
      )}
    </TabContext.Consumer>
  );
};

export default SearchInputs;
